// Search screen. Ported from designproject/files/screens-card.jsx
// (`SearchScreen`) to Preact + TS strict. Phase 2.5 (hh-7q2).
//
// Two query modes plus a voice entry point:
//   Имя       — GET /api/v1/items?q=...        (ILIKE name match)
//   Семантика — POST /api/v1/search/semantic   (NL lookup; lexical_fallback
//               on the current backend — UI just renders `results`)
//   Голос     — mic button → `onOpenVoice` callback; the parent owns VoiceModal.
//
// A row tap navigates to the item card via `router.go('item_card', { id })`.

import type { JSX } from 'preact';
import { useCallback, useRef, useState } from 'preact/hooks';
import { T, Icon, SectionLabel, entityIcon } from '../../tokens';
import type { RouterApi } from '../../router';
import { items as itemsApi, search as searchApi, humanizeError } from '../../api';

type Mode = 'name' | 'semantic';

interface SearchScreenProps {
  readonly router: RouterApi;
  readonly onOpenVoice?: () => void;
}

/** A backend-agnostic row: name + a single readable location line. */
interface ResultRow {
  readonly id: string;
  readonly name: string;
  readonly location: string;
  /** Manually picked icon name, or null/undefined for auto-resolve (hh-345). */
  readonly icon?: string | null;
  /** Semantic mode only — short why-this-matched note. */
  readonly reason?: string;
}

type ResultState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly query: string;
      readonly rows: readonly ResultRow[];
    };

export function SearchScreen({
  router,
  onOpenVoice,
}: SearchScreenProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<Mode>('name');
  const [state, setState] = useState<ResultState>({ kind: 'idle' });
  // Bumped on each submit so a stale in-flight request can't overwrite newer
  // results (last-write-wins).
  const runIdRef = useRef(0);

  const run = useCallback(
    (rawQuery: string, runMode: Mode): void => {
      const q = rawQuery.trim();
      if (q.length === 0) {
        setState({ kind: 'idle' });
        return;
      }
      const runId = (runIdRef.current += 1);
      setState({ kind: 'loading' });
      void executeSearch(q, runMode)
        .then((rows) => {
          if (runIdRef.current !== runId) return;
          setState({ kind: 'ready', query: q, rows });
        })
        .catch((err: unknown) => {
          if (runIdRef.current !== runId) return;
          setState({ kind: 'error', message: describeError(err) });
        });
    },
    [],
  );

  const submit = (): void => run(query, mode);

  const switchMode = (next: Mode): void => {
    if (next === mode) return;
    setMode(next);
    // Re-run the active query under the new mode so the toggle feels live.
    if (query.trim().length > 0) run(query, next);
  };

  return (
    <div style={containerStyle}>
      <div style={{ padding: '8px 16px 0' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={searchBoxStyle}
        >
          <Icon name="search" size={18} color={T.ink3} />
          <input
            value={query}
            onInput={(e) => setQuery(e.currentTarget.value)}
            placeholder="Имя или описание…"
            aria-label="Поисковый запрос"
            enterkeyhint="search"
            style={inputStyle}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setState({ kind: 'idle' });
              }}
              aria-label="Очистить"
              style={clearButtonStyle}
            >
              <Icon name="close" size={14} color={T.ink3} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onOpenVoice?.()}
            aria-label="Поиск голосом"
            disabled={onOpenVoice === undefined}
            style={micButtonStyle}
          >
            <Icon name="mic" size={16} color={T.brandDeep} />
          </button>
        </form>

        <div style={chipsRowStyle}>
          <ModeChip
            label="Имя"
            active={mode === 'name'}
            onClick={() => switchMode('name')}
          />
          <ModeChip
            label="Семантика"
            active={mode === 'semantic'}
            onClick={() => switchMode('semantic')}
          />
        </div>
      </div>

      <div style={scrollStyle}>
        <ResultArea state={state} mode={mode} router={router} />
      </div>
    </div>
  );
}

// ─── Result area ─────────────────────────────────────────────────────────────

interface ResultAreaProps {
  readonly state: ResultState;
  readonly mode: Mode;
  readonly router: RouterApi;
}

function ResultArea({ state, mode, router }: ResultAreaProps): JSX.Element {
  if (state.kind === 'idle') {
    return (
      <EmptyNote
        emoji="🔎"
        title="Введи запрос"
        hint={
          mode === 'name'
            ? 'Поиск вещи по имени'
            : 'Опиши вещь своими словами'
        }
      />
    );
  }
  if (state.kind === 'loading') {
    return <CenteredNote text="Ищу…" />;
  }
  if (state.kind === 'error') {
    return <CenteredNote text={`Не удалось найти: ${state.message}`} tone="danger" />;
  }
  if (state.rows.length === 0) {
    return (
      <EmptyNote
        emoji="🔎"
        title={`Ничего не нашлось по «${state.query}»`}
        hint="Попробуй другое слово или переключи режим"
      />
    );
  }
  return (
    <>
      <SectionLabel>Совпадения · {state.rows.length}</SectionLabel>
      {state.rows.map((row) => (
        <ResultRowView
          key={row.id}
          row={row}
          onClick={() => router.go('item_card', { id: row.id })}
        />
      ))}
    </>
  );
}

interface ResultRowViewProps {
  readonly row: ResultRow;
  readonly onClick: () => void;
}

function ResultRowView({ row, onClick }: ResultRowViewProps): JSX.Element {
  return (
    <div onClick={onClick} style={rowStyle}>
      {/* hh-345: ручная иконка вещи либо авто-подбор по названию. */}
      <Icon
        name={entityIcon('item', { icon: row.icon, name: row.name })}
        size={18}
        color={T.ink3}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={rowNameStyle}>{row.name}</div>
        {row.location.length > 0 && (
          <div style={rowSubStyle}>{row.location}</div>
        )}
        {row.reason !== undefined && row.reason.length > 0 && (
          <div style={rowReasonStyle}>
            <Icon name="spark" size={12} color={T.brandDeep} />
            {row.reason}
          </div>
        )}
      </div>
      <Icon name="chevR" size={16} color={T.ink4} />
    </div>
  );
}

// ─── Mode chip ───────────────────────────────────────────────────────────────

interface ModeChipProps {
  readonly label: string;
  readonly active: boolean;
  readonly onClick: () => void;
}

function ModeChip({ label, active, onClick }: ModeChipProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '6px 12px',
        borderRadius: 999,
        background: active ? T.ink : T.surface,
        color: active ? '#fff' : T.ink2,
        border: `1px solid ${active ? T.ink : T.border}`,
        fontSize: 12.5,
        fontWeight: 650,
        fontFamily: T.font,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// ─── Small parts ─────────────────────────────────────────────────────────────

interface EmptyNoteProps {
  readonly emoji: string;
  readonly title: string;
  readonly hint: string;
}

function EmptyNote({ emoji, title, hint }: EmptyNoteProps): JSX.Element {
  return (
    <div style={emptyStyle}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{emoji}</div>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: T.ink2 }}>
        {title}
      </div>
      <div style={{ fontSize: 12.5, marginTop: 6, color: T.ink3 }}>{hint}</div>
    </div>
  );
}

interface CenteredNoteProps {
  readonly text: string;
  readonly tone?: 'danger';
}

function CenteredNote({ text, tone }: CenteredNoteProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        padding: '40px 24px',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 500,
        color: tone === 'danger' ? T.danger : T.ink3,
      }}
    >
      {text}
    </div>
  );
}

// ─── Data ────────────────────────────────────────────────────────────────────

async function executeSearch(q: string, mode: Mode): Promise<readonly ResultRow[]> {
  if (mode === 'name') {
    const res = await itemsApi.searchItems(q);
    return res.items.map((hit) => ({
      id: hit.id,
      name: hit.name,
      location: hit.location,
      icon: hit.icon ?? null,
    }));
  }
  const res = await searchApi.semanticSearch(q);
  return res.results.map((hit) => ({
    id: hit.itemId,
    name: hit.name,
    location: hit.locationLabel,
    icon: hit.icon ?? null,
    reason: hit.reason,
  }));
}

function describeError(err: unknown): string {
  return humanizeError(err, 'load');
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const containerStyle: JSX.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
};

const searchBoxStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 14px',
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 14,
};

const inputStyle: JSX.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: T.font,
  fontSize: 15,
  color: T.ink,
};

const clearButtonStyle: JSX.CSSProperties = {
  width: 24,
  height: 24,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 12,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const micButtonStyle: JSX.CSSProperties = {
  width: 32,
  height: 32,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: T.brandSoft,
  border: 'none',
  borderRadius: 16,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0,
};

const chipsRowStyle: JSX.CSSProperties = {
  display: 'flex',
  gap: 6,
  marginTop: 10,
};

const scrollStyle: JSX.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: '6px 16px 24px',
};

const rowStyle: JSX.CSSProperties = {
  background: T.surface,
  border: `1px solid ${T.border}`,
  borderRadius: 12,
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginTop: 8,
  cursor: 'pointer',
};

const rowNameStyle: JSX.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: T.ink,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowSubStyle: JSX.CSSProperties = {
  fontSize: 12,
  color: T.ink3,
  marginTop: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowReasonStyle: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  color: T.brandDeep,
  fontWeight: 600,
  marginTop: 3,
};

const emptyStyle: JSX.CSSProperties = {
  textAlign: 'center',
  padding: '40px 20px',
  color: T.ink3,
};
